// Percorso file: src/pages/GamePreparePage.jsx
//
// LA PARTITA — cantiere GAMES: fetta G1 (la preparazione / convocazione,
// PIANO-GAMES §2 fase 2 e §5.3) estesa dalla fetta G2 (il cuore: campo dal
// vivo, chiusura, riepilogo — PIANO §2 fasi 3-7, §3, §5.4-5.5).
//
// ⚠️ UNA PAGINA, QUATTRO FASI, NESSUNA ROTTA IN PIÙ (incarico G2 §4). La
// partita vive TUTTA nello stato React di questa pagina — convocati,
// formazione, cronometro, minuti, eventi — e preparazione → campo →
// chiusura → riepilogo sono FASI della stessa pagina, non sotto-pagine:
//
//   "prepara"   la rosa con lo stato di Attendance e la spunta «convocato»
//               (G1): il coach sceglie chi porta in partita;
//   "gioca"     il campo dal vivo: schiera col trascinamento, avvia il
//               cronometro, cambia, registra gol/gialli/rossi (G2);
//   "chiudi"    il risultato scritto a mano e la tabella dei totali
//               editabile, poi «Save» → POST /games (G2);
//   "riepilogo" la partita conclusa letta dal cassetto `games`, con «Edit»
//               che riporta alla tabella e risalva lo stesso record (G2).
//
// All'ingresso la pagina RIPARTE DA CAPO: fase «prepara» se nel cassetto non
// c'è un record per questo evento, «riepilogo» se c'è.
//
// ⚠️ PERSISTENZA «CASO A» (decisione del committente, incarico G2 §6): la
// partita in corso vive SOLO IN MEMORIA. Niente localStorage, sessionStorage
// né IndexedDB — coerente con la regola di casa (commit be12170: «lo stato
// dura quanto la pagina, nessuna scrittura sul dispositivo — dati di
// minori»): gli id dei convocati sono id di documento di MINORI e non
// finiscono su disco. Conseguenze, dette a schermo: i dati NON sono salvati
// finché il coach non preme «Save» a fine partita; chiudere o ricaricare
// l'app perde la partita in corso. Perciò un AVVISO sempre visibile nelle
// fasi gioca/chiudi, una CONFERMA sul «Back» del telaio E sul tasto
// «indietro» del browser (la guardia di G2b, una voce di cronologia in più
// finché la partita è viva), e — rete extra — un `beforeunload` che avverte
// dove il browser lo consente, sapendo che NON salva e che su mobile è
// inaffidabile. Nessun salvataggio automatico
// alla chiusura: sarebbe una falsa sicurezza (la chiamata non è garantita).
// Lo stato «in corso» è effimero: per la lista una partita resta «da
// giocare» finché non è salvata.
//
// ⚠️ SQUADRA E PARTITA ARRIVANO DAL PARAMETRO DI ROTTA
// (`/games/team/:teamName/prepare/:eventId`). Niente `location.state`.
//
// ⚠️ IL GATE STA A MONTE, componente esterno senza effetti (AttendanceSummary).
//
// ⚠️ TRE LETTURE, TUTTE ESISTENTI O GIÀ SCRITTE (nessuna rotta nuova qui):
//   · `GET /attendance/team/:teamName` → `{ players, attendance }` — la rosa
//     (id, firstName, lastName, gender, dob) e le presenze dell'anno
//     operativo, riservata ai coach della squadra. LA CHIAVE DI UN
//     GIOCATORE È `p.id`, l'id del suo DOCUMENTO;
//   · `GET /events` — nudo, per ritrovare QUESTA partita;
//   · `GET /games/team/:teamName` — le partite SALVATE della squadra (F1),
//     per sapere se questa è già conclusa.
// UNA SCRITTURA (F1): `POST /games` con `{ eventId, score, players }` — solo
// i convocati; team/date/season li mette il backend.
//
// IL FILTRO DELL'EVENTO (nodo lasciato da G1, chiuso qui): in partita si
// entra SOLO per un evento `type === "Game"` dell'anno operativo — lo stesso
// metro della lista. Altrimenti «This game is not available.»: non si
// prepara un allenamento né una partita di un anno passato.
//
// Le REGOLE DI GIOCO (cronometro, minuti, eventi, corpo del salvataggio)
// sono le funzioni pure di components/games/regolePartita.js, provate in
// un banco Node; i sotto-componenti (CampoPartita, Cronometro, MenuEvento,
// TabellaFinale, RiepilogoPartita) sono di sola presentazione: lo stato
// sta qui e scende come props.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaBan,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaFutbol,
  FaNotesMedical,
  FaPen,
  FaPlay,
  FaSave,
  FaSquare,
  FaTimes,
  FaUndo
} from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { isActingAs } from "../utils/roleUtils";
import { eDiUnaMiaSquadra } from "../utils/squadre";
import { dataEvento } from "../utils/calendario";
// `messaggioDelServer` legge il `{ error }` del corpo d'errore di api.js
// (MemberProfileModal.jsx lo importa dallo stesso modulo).
import { messaggioDelServer } from "../utils/enrolDati";
import CampoPartita from "../components/games/CampoPartita";
import Cronometro from "../components/games/Cronometro";
import MenuEvento from "../components/games/MenuEvento";
import TabellaFinale from "../components/games/TabellaFinale";
import RiepilogoPartita from "../components/games/RiepilogoPartita";
import {
  avanzaCronometro,
  avviaCronometro,
  azzeraEventiGiocatore,
  CENTRO_CAMPO,
  chiudiPartita,
  chiudiTempo,
  corpoSalvataggio,
  entraInCampo,
  esceDalCampo,
  nuovaPartita,
  pausaCronometro,
  registraEvento,
  scambia,
  spostaInCampo,
  statistiche,
  TIPI_EVENTO
} from "../components/games/regolePartita";
// La spunta di casa (`.qpsc-check`), il pulsante compatto §5.1 e il guscio
// modale vivono in AdminTools.css: si importa il foglio per dichiararne la
// dipendenza, come fa EnrolNextSeason.jsx.
import "../components/AdminTools.css";
import "../components/games/GamesMatch.css";
import "./Games.css";

// I cinque stati di Attendance, nell'ordine e con le etichette che il socio
// legge: copia dichiarata di `STATI` in AttendanceSummary.jsx (il valore è
// quello che il backend scrive; le icone sono Font Awesome, mai emoji).
const STATI = [
  { valore: "yes", etichetta: "Present", Icon: FaCheck },
  { valore: "no", etichetta: "Absent", Icon: FaTimes },
  { valore: "pending", etichetta: "Pending", Icon: FaClock },
  { valore: "injured", etichetta: "Injured", Icon: FaNotesMedical },
  { valore: "suspended", etichetta: "Suspended", Icon: FaBan }
];

const STATO_DI = Object.fromEntries(STATI.map((s) => [s.valore, s]));

// «Nessuna risposta»: il giocatore non ha un record per questa partita.
const NESSUNA_RISPOSTA = "No answer";

// ===========================================================================
// AIUTANTI DI DATA — `dataEvento` dalla sede unica utils/calendario.js
// (giro G-K1)
// ===========================================================================

/** Giorno + data brevi, la stessa forma delle card del calendario. */
function etichettaBreve(ev) {
  const d = dataEvento(ev);
  if (!d) return String(ev?.date || "");

  return `${d.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()} ${d.toLocaleDateString(
    "en-AU",
    { day: "2-digit", month: "2-digit" }
  )}`;
}

/** L'anno di un evento, dalla sua data (gli eventi non portano la stagione).
 *  Copia di GamesListPage.jsx. */
function annoEvento(ev) {
  const d = dataEvento(ev);
  return d ? d.getFullYear() : null;
}

/** Il criterio «è una partita» di GAMES: affermativo, deciso dal committente
 *  (copia di GamesListPage.jsx). */
const ePartita = (ev) => ev && ev.type === "Game";

const uguali = (a, b) => String(a) === String(b);

const nomeDi = (p) => `${p.firstName || ""} ${p.lastName || ""}`.trim();

/** Il nome corto del token in campo: nome e iniziale del cognome. */
function nomeBreve(p) {
  const nome = String(p.firstName || "").trim();
  const cognome = String(p.lastName || "").trim();
  if (!nome) return cognome || "Player";
  return cognome ? `${nome} ${cognome[0]}.` : nome;
}

/** Che cosa dire di un salvataggio non riuscito: si distingue il RIFIUTO
 *  (`err.status` c'è) dal GUASTO di rete (non c'è), come `messaggioDiErrore`
 *  di enrolDati.js; i codici sono quelli di F1 (400/403/404/409). */
function messaggioSalvataggio(err) {
  const stato = err && err.status;
  if (!stato) {
    return "Network problem — nothing was saved. Check the connection and try again.";
  }
  const dalServer = messaggioDelServer(err);
  if (stato === 400) {
    return `Some values were not accepted${dalServer ? ` (${dalServer})` : ""}. Nothing was saved.`;
  }
  if (stato === 403) return "You are not allowed to save this game.";
  if (stato === 404) return "This game no longer exists in the calendar.";
  if (stato === 409) return dalServer || "The season is not available right now.";
  return "Could not save the match. Try again.";
}

/** Le righe editabili della tabella finale da un elenco di totali (i
 *  numeri diventano il testo che il coach vede e corregge). */
function righeDaTotali(totali, nomi) {
  return totali.map((t) => ({
    playerId: String(t.playerId),
    nome: nomi.get(String(t.playerId)) || "Former player",
    minutes: String(t.minutes ?? 0),
    goals: String(t.goals ?? 0),
    yellowCards: String(t.yellowCards ?? 0),
    redCard: Boolean(t.redCard)
  }));
}

// ---------------------------------------------------------------------------
// LA VOCE DI GUARDIA della cronologia (G2b — vedi «LA GUARDIA DEL TASTO
// INDIETRO» nella pagina). Lo stato spinto copia quello del router
// (`{ usr, key, idx }` di @remix-run/router, `getUrlBasedHistory`) e vi
// aggiunge un contrassegno: così `location.key` — su cui `esci` decide —
// non cambia scavalcando la guardia, e la voce si riconosce anche a un
// montaggio successivo. L'URL è lo stesso della pagina: scavalcarla non
// cambia rotta e non smonta nulla.
// ---------------------------------------------------------------------------
const CONTRASSEGNO_GUARDIA = "qpscGuardiaPartita";

const suVoceDiGuardia = () =>
  Boolean(window.history.state && window.history.state[CONTRASSEGNO_GUARDIA]);

function armaVoceDiGuardia() {
  if (suVoceDiGuardia()) return;
  const stato = window.history.state || {};
  const idx = typeof stato.idx === "number" ? { idx: stato.idx + 1 } : {};
  window.history.pushState(
    { ...stato, ...idx, [CONTRASSEGNO_GUARDIA]: true },
    "",
    window.location.href
  );
}

/** Finché la guardia è armata lo scroll non si «ripristina» al pop
 *  (scavalcare la guardia riporterebbe la pagina a dov'era all'armamento);
 *  il valore di prima si rimette quando la guardia cade. */
function bloccaScroll(g) {
  if (g.scroll != null) return;
  g.scroll = window.history.scrollRestoration;
  window.history.scrollRestoration = "manual";
}

function liberaScroll(g) {
  if (g.scroll == null) return;
  window.history.scrollRestoration = g.scroll;
  g.scroll = null;
}

// ===========================================================================
// IL CANCELLO — componente esterno, senza effetti e senza chiamate
// ===========================================================================
export default function GamePreparePage() {
  const { user, activeContext } = useAuth() || {};
  const { teamName, eventId } = useParams();
  const squadra = String(teamName || "").trim();
  const idPartita = String(eventId || "").trim();

  const indossaCoach = isActingAs(user, activeContext, "COACH");

  if (!user || !indossaCoach) {
    return (
      <HomeShell backTo="/home" showSwitch={false}>
        <h1 className="qpsc-page-title">Games</h1>
        <p className="qpsc-body-text">Unauthorized access.</p>
      </HomeShell>
    );
  }

  // Indirizzo digitato a metà: si dice da dove si apre, e NON si indovina
  // nulla al posto suo. Nessuna chiamata parte.
  if (!squadra || !idPartita) {
    return (
      <HomeShell backTo="/home/coach" showSwitch={false}>
        <h1 className="qpsc-page-title">Games</h1>
        <p className="qpsc-body-text">
          Open a game from your team's games list to prepare it.
        </p>
      </HomeShell>
    );
  }

  return <Partita squadra={squadra} idPartita={idPartita} />;
}

// ===========================================================================
// LA PAGINA — montata solo per chi ha superato il cancello
// ===========================================================================
function Partita({ squadra, idPartita }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { operatingSeason } = useAuth() || {};
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  const rottaLista = `/games/team/${encodeURIComponent(squadra)}`;

  // "caricamento" | "pronto" | "errore" — lo stato a tre valori di
  // EditMemberPage.jsx.
  const [stato, setStato] = useState("caricamento");
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [evento, setEvento] = useState(null);

  // Il record del cassetto `games` per questo evento (null = da giocare).
  const [record, setRecord] = useState(null);

  // LA FASE della pagina (vedi testa del file).
  const [fase, setFase] = useState("prepara");

  // I CONVOCATI: un insieme di id di documento, IN MEMORIA. Default (G1):
  // i «presenti» (`yes`) di questa partita; il coach aggiunge e toglie
  // liberamente. Si ricalcola a ogni lettura, mai da storage.
  const [convocati, setConvocati] = useState(() => new Set());

  // LA PARTITA DAL VIVO (regolePartita.js): viva in «gioca», chiusa in
  // «chiudi» (i totali sono già in `righe`), null altrove.
  const [partita, setPartita] = useState(null);
  // L'istante (ms) con cui si legge il cronometro: rinfrescato una volta al
  // secondo mentre corre, e ad ogni gesto.
  const [adesso, setAdesso] = useState(() => Date.now());
  // Il giocatore col menù eventi aperto (id), o null.
  const [menuPer, setMenuPer] = useState(null);
  // La conferma in attesa: "" | "half" | "end" | "leave".
  const [conferma, setConferma] = useState("");

  // LA CHIUSURA: risultato e righe (testo, come lo digita il coach).
  const [score, setScore] = useState({ us: "", them: "" });
  const [righe, setRighe] = useState([]);
  // Vero quando si sta modificando un record già salvato (da «Edit»):
  // «Cancel» torna al riepilogo, e non c'è una partita in corso da perdere.
  const [modificaDaRecord, setModificaDaRecord] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);
  const [erroreSalvataggio, setErroreSalvataggio] = useState("");

  // -----------------------------------------------------------------------
  // I DATI — tre letture insieme; guardia `vivo` prima di ogni scrittura.
  // -----------------------------------------------------------------------
  useEffect(() => {
    let vivo = true;

    window.scrollTo(0, 0);
    setStato("caricamento");
    setPlayers([]);
    setAttendance([]);
    setEvento(null);
    setRecord(null);
    setConvocati(new Set());
    setPartita(null);
    setFase("prepara");
    setMenuPer(null);
    setConferma("");
    setErroreSalvataggio("");

    Promise.all([
      api.get(`/attendance/team/${encodeURIComponent(squadra)}`),
      api.get("/events"),
      api.get(`/games/team/${encodeURIComponent(squadra)}`)
    ])
      .then(([dati, tuttiGliEventi, partiteSalvate]) => {
        if (!vivo) return;

        const rosa = Array.isArray(dati?.players) ? dati.players : [];
        const presenze = Array.isArray(dati?.attendance) ? dati.attendance : [];
        const eventi = Array.isArray(tuttiGliEventi) ? tuttiGliEventi : [];
        const salvate = Array.isArray(partiteSalvate) ? partiteSalvate : [];

        // La partita è QUESTO evento, e dev'essere di QUESTA squadra: un id
        // di un'altra squadra digitato nell'indirizzo non apre una
        // preparazione con la rosa di questa — si respinge con la riga
        // unica, come per un id inesistente.
        const ev =
          eventi.find(
            (e) => e && uguali(e.id, idPartita) && eDiUnaMiaSquadra(e.team, [squadra])
          ) || null;

        if (!ev) {
          setStato("errore");
          return;
        }

        const presentiQui = new Set(
          presenze
            .filter((v) => v && uguali(v.eventId, idPartita) && v.status === "yes")
            .map((v) => String(v.playerUid))
        );

        const salvata = salvate.find((r) => r && uguali(r.eventId, idPartita)) || null;

        setPlayers(rosa);
        setAttendance(presenze);
        setEvento(ev);
        setRecord(salvata);
        setConvocati(new Set(rosa.filter((p) => presentiQui.has(String(p.id))).map((p) => String(p.id))));
        setFase(salvata ? "riepilogo" : "prepara");
        setStato("pronto");
      })
      .catch(() => {
        // Un solo esito per ogni rifiuto (403, 409, guasto tecnico): la
        // pagina non deve lasciar capire se quella squadra o quella partita
        // esistano.
        if (!vivo) return;
        setStato("errore");
      });

    return () => {
      vivo = false;
    };
  }, [squadra, idPartita]);

  // -----------------------------------------------------------------------
  // IL BATTITO DEL CRONOMETRO — un rinfresco al secondo mentre corre, e uno
  // al ritorno in primo piano (lo schermo spento congela i timer, non il
  // conto, che si rifà da `avviatoA`).
  // -----------------------------------------------------------------------
  const cronCorre = Boolean(partita && partita.cron.inCorso);
  useEffect(() => {
    if (!cronCorre) return undefined;

    const battito = setInterval(() => setAdesso(Date.now()), 1000);
    const alRitorno = () => setAdesso(Date.now());
    document.addEventListener("visibilitychange", alRitorno);
    return () => {
      clearInterval(battito);
      document.removeEventListener("visibilitychange", alRitorno);
    };
  }, [cronCorre]);

  // -----------------------------------------------------------------------
  // LA RETE EXTRA — `beforeunload` nelle fasi in cui c'è qualcosa da
  // perdere. Avverte, NON salva (incarico G2 §6).
  // -----------------------------------------------------------------------
  const partitaViva = fase === "gioca" || fase === "chiudi";
  useEffect(() => {
    if (!partitaViva) return undefined;

    const avverti = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avverti);
    return () => window.removeEventListener("beforeunload", avverti);
  }, [partitaViva]);

  // IL «BACK»: la guardia di react-router v6 di EditMemberPage.jsx
  // (`location.key === "default"` = prima voce della cronologia → si ripiega
  // sulla lista della squadra). In G2, con una partita viva, prima si chiede.
  const esci = useCallback(() => {
    if (location.key === "default") {
      navigate(rottaLista, { replace: true });
    } else {
      navigate(-1);
    }
  }, [location.key, navigate, rottaLista]);

  const tornaIndietro = useCallback(() => {
    if (partitaViva && !modificaDaRecord) {
      setConferma("leave");
      return;
    }
    esci();
  }, [partitaViva, modificaDaRecord, esci]);

  // -----------------------------------------------------------------------
  // LA GUARDIA DEL TASTO INDIETRO (G2b). Il tasto «indietro» del browser e
  // lo swipe di sistema sono una navigazione INTERNA di react-router
  // (`BrowserRouter`, App.jsx): `beforeunload` non scatta e `useBlocker`
  // non c'è senza un data router — la pagina si smonterebbe e la partita
  // viva si perderebbe SENZA avviso. Tecnica: finché c'è una partita da
  // proteggere si tiene sopra la voce corrente una VOCE DI GUARDIA
  // (`history.pushState` sullo stesso URL, contrassegnata — helper di
  // modulo). «Indietro» scavalca la guardia e resta su questa pagina
  // (stesso URL, stessa rotta: il router rilegge la voce e non smonta
  // nulla); il `popstate` che ne nasce RIARMA subito la guardia e apre la
  // STESSA finestra del «Back» del telaio (`conferma === "leave"`): «Stay»
  // la chiude e basta (la guardia è già riarmata), «Leave» passa da
  // `abbandona`, che scavalca la guardia e poi esce come farebbe `esci`.
  // Una finestra sola, uno stato solo: Back del telaio e tasto indietro
  // finiscono nello stesso `conferma`, e il «Leave» è uno.
  //
  // La guardia vive solo con `daProteggere` (partita viva E non in modifica
  // di un record salvato: lì si perdono solo le modifiche, il record c'è).
  // Fuori di lì nessuna voce in più e nessuna conferma: il tasto indietro è
  // quello di sempre. Quando la partita cessa d'essere da proteggere
  // (salvata, «Back to squad») la guardia si toglie da sola, in silenzio,
  // con un passo indietro sullo stesso URL. Lo stato della guardia sta in
  // un `ref` perché l'ascoltatore di `popstate` è montato una volta sola.
  //
  // ⚠️ NON è una copertura totale: su qualche browser mobile il gesto di
  // sistema può sfuggire a `popstate`, e le altre uscite dal telaio
  // (UserBar, logout) non passano di qui. La rete di fondo resta l'avviso
  // sempre visibile.
  // -----------------------------------------------------------------------
  const daProteggere = partitaViva && !modificaDaRecord;
  const guardia = useRef({
    // Vero quando la voce corrente è la nostra guardia — anche una lasciata
    // da un montaggio precedente: allora «indietro» prosegue con `esci`.
    armata: suVoceDiGuardia(),
    daProteggere: false,
    // Il passo indietro silenzioso con cui la guardia si toglie da sola.
    silenzio: false,
    // «Leave» in corso: cosa fare una volta scavalcata la guardia (o null).
    uscendo: null,
    // Il percorso su cui la guardia è armata: un pop che porta altrove non
    // è nostro (la pagina si smonta e basta).
    percorso: "",
    // Il `scrollRestoration` da rimettere quando la guardia cade.
    scroll: null
  });
  const esciRef = useRef(esci);
  useEffect(() => {
    esciRef.current = esci;
  }, [esci]);

  useEffect(() => {
    const g = guardia.current;
    const alPop = () => {
      if (!g.armata) return; // non è la nostra voce: navigazione normale
      g.armata = false; // la guardia è stata scavalcata
      if (g.percorso && window.location.pathname !== g.percorso) {
        // Si è andati altrove (un salto lungo nella cronologia): non si
        // tocca nulla, la pagina si smonta.
        liberaScroll(g);
        return;
      }
      if (g.uscendo) {
        const prosegui = g.uscendo;
        g.uscendo = null;
        liberaScroll(g);
        prosegui();
        return;
      }
      if (g.silenzio) {
        g.silenzio = false;
        if (g.daProteggere) {
          // Nel frattempo la partita è di nuovo da proteggere (StrictMode
          // in sviluppo, o un rientro in campo): si riarma.
          armaVoceDiGuardia();
          g.armata = true;
        } else {
          liberaScroll(g);
        }
        return;
      }
      if (!g.daProteggere) {
        // Una guardia stantia (montaggio precedente): «indietro» prosegue.
        liberaScroll(g);
        esciRef.current();
        return;
      }
      // «Indietro» con una partita viva: si riarma e si chiede.
      armaVoceDiGuardia();
      g.armata = true;
      setMenuPer(null);
      setConferma("leave");
    };
    window.addEventListener("popstate", alPop);
    return () => window.removeEventListener("popstate", alPop);
  }, []);

  useEffect(() => {
    const g = guardia.current;
    g.daProteggere = daProteggere;
    if (!daProteggere) return undefined;

    bloccaScroll(g);
    armaVoceDiGuardia();
    g.armata = true;
    g.percorso = window.location.pathname;
    return () => {
      g.daProteggere = false;
      // La partita non è più da proteggere, o la pagina si smonta: se la
      // voce corrente è ancora la guardia (stesso URL) la si scavalca in
      // silenzio; se non lo è (si è già usciti, o si è andati altrove) la
      // cronologia non si tocca.
      if (g.uscendo) return;
      if (g.armata && suVoceDiGuardia()) {
        g.silenzio = true;
        window.history.back();
      } else {
        liberaScroll(g);
      }
    };
  }, [daProteggere]);

  // «Leave» — dalla finestra, l'abbia aperta il Back del telaio o il tasto
  // indietro: si scavalca la guardia (se c'è) e si esce come `esci`. Con la
  // guardia armata la voce della pagina sta SOTTO la guardia: «una voce
  // indietro» è la pagina stessa, per uscire davvero ne serve una in più
  // (`go(-2)`, una traversata sola) o — prima voce della cronologia — un
  // passo indietro e poi la lista al posto della pagina, come fa `esci`.
  const abbandona = useCallback(() => {
    const g = guardia.current;
    if (!g.armata || !suVoceDiGuardia()) {
      esci();
      return;
    }
    if (location.key === "default") {
      g.uscendo = () => navigate(rottaLista, { replace: true });
      window.history.back();
    } else {
      g.uscendo = () => {};
      liberaScroll(g);
      window.history.go(-2);
    }
  }, [esci, location.key, navigate, rottaLista]);

  // -----------------------------------------------------------------------
  // DERIVATI
  // -----------------------------------------------------------------------
  // Lo stato di Attendance di ogni giocatore per QUESTA partita.
  const statoPerGiocatore = useMemo(() => {
    const mappa = new Map();
    for (const voce of attendance) {
      if (!voce || !uguali(voce.eventId, idPartita)) continue;
      mappa.set(String(voce.playerUid), voce.status);
    }
    return mappa;
  }, [attendance, idPartita]);

  const statoDi = (playerId) => statoPerGiocatore.get(String(playerId)) || null;

  // playerId → nome, dalla rosa dell'anno (per tabella e riepilogo).
  const nomi = useMemo(() => new Map(players.map((p) => [String(p.id), nomeDi(p)])), [players]);

  // I convocati nell'ordine della rosa, con nome intero e nome corto:
  // ciò che il campo e la tabella mostrano.
  const giocatoriPartita = useMemo(
    () =>
      players
        .filter((p) => convocati.has(String(p.id)))
        .map((p) => ({ id: String(p.id), nome: nomeDi(p), breve: nomeBreve(p) })),
    [players, convocati]
  );

  // Il filtro dell'evento: solo un «Game» dell'anno operativo (come la
  // lista). Con l'anno ancora ignoto non si decide.
  const disponibile =
    evento && annoOperativo != null && ePartita(evento) && annoEvento(evento) === annoOperativo;

  const quando = evento
    ? `${etichettaBreve(evento)}${evento.time ? ` ${evento.time}` : ""}`
    : "";

  // -----------------------------------------------------------------------
  // FASE «PREPARA»
  // -----------------------------------------------------------------------
  const commuta = (playerId) => {
    const chiave = String(playerId);
    setConvocati((prima) => {
      const dopo = new Set(prima);
      if (dopo.has(chiave)) dopo.delete(chiave);
      else dopo.add(chiave);
      return dopo;
    });
  };

  const avviaPartita = () => {
    setPartita(nuovaPartita(giocatoriPartita.map((g) => g.id)));
    setAdesso(Date.now());
    setMenuPer(null);
    setFase("gioca");
    window.scrollTo(0, 0);
  };

  // -----------------------------------------------------------------------
  // FASE «GIOCA» — ogni gesto passa dalle funzioni pure, con l'istante letto
  // ADESSO (`Date.now()`), non da quello del render.
  // -----------------------------------------------------------------------
  const aggiorna = (trasforma) => {
    const ora = Date.now();
    setPartita((p) => (p ? trasforma(p, ora) : p));
    setAdesso(ora);
  };

  // Prima del fischio d'inizio (niente tempo corso, niente eventi) si può
  // tornare alla rosa senza perdere nulla: i convocati restano.
  const maiCominciata = Boolean(
    partita &&
      partita.cron.tempo === 1 &&
      !partita.cron.inCorso &&
      partita.cron.accumulato === 0 &&
      partita.eventi.length === 0
  );

  const tornaAllaRosa = () => {
    setPartita(null);
    setMenuPer(null);
    setFase("prepara");
  };

  const giocatoreDelMenu = menuPer ? giocatoriPartita.find((g) => g.id === menuPer) : null;

  // La chiusura: cronometro fermo, intervalli chiusi, totali in tabella.
  const terminaPartita = () => {
    const ora = Date.now();
    const chiusa = chiudiPartita(partita, ora);
    const totali = statistiche(chiusa, giocatoriPartita.map((g) => g.id), ora);
    setPartita(chiusa);
    setAdesso(ora);
    setRighe(righeDaTotali(totali, nomi));
    setScore({ us: "", them: "" });
    setModificaDaRecord(false);
    setErroreSalvataggio("");
    setMenuPer(null);
    setFase("chiudi");
    window.scrollTo(0, 0);
  };

  // -----------------------------------------------------------------------
  // FASE «CHIUDI» — tabella e salvataggio
  // -----------------------------------------------------------------------
  const cambiaRiga = (playerId, campo, valore) => {
    setRighe((prima) => prima.map((r) => (r.playerId === playerId ? { ...r, [campo]: valore } : r)));
  };

  const cambiaScore = (lato, valore) => setScore((s) => ({ ...s, [lato]: valore }));

  const salva = () => {
    const esito = corpoSalvataggio(idPartita, score, righe);
    if (!esito.ok) {
      setErroreSalvataggio(esito.errore);
      return;
    }

    setSalvataggio(true);
    setErroreSalvataggio("");

    api
      .post("/games", esito.corpo)
      .then(() => {
        // La risposta è `{ ok: true }`: il record mostrato è ciò che si è
        // appena inviato (team/date/season li ha messi il backend e qui non
        // servono).
        setRecord({
          eventId: esito.corpo.eventId,
          score: esito.corpo.score,
          players: esito.corpo.players
        });
        setPartita(null);
        setModificaDaRecord(false);
        setFase("riepilogo");
        window.scrollTo(0, 0);
      })
      .catch((err) => {
        setErroreSalvataggio(messaggioSalvataggio(err));
      })
      .finally(() => setSalvataggio(false));
  };

  // -----------------------------------------------------------------------
  // FASE «RIEPILOGO» — «Edit» riporta alla tabella, dal record salvato.
  // -----------------------------------------------------------------------
  const modificaRecord = () => {
    if (!record) return;
    setRighe(righeDaTotali(Array.isArray(record.players) ? record.players : [], nomi));
    setScore({
      us: record.score && record.score.us != null ? String(record.score.us) : "",
      them: record.score && record.score.them != null ? String(record.score.them) : ""
    });
    setModificaDaRecord(true);
    setErroreSalvataggio("");
    setFase("chiudi");
    window.scrollTo(0, 0);
  };

  const annullaModifica = () => {
    setErroreSalvataggio("");
    setFase("riepilogo");
  };

  // -----------------------------------------------------------------------
  // LA RESA
  // -----------------------------------------------------------------------
  // La conferma d'uscita con una partita viva — la stessa finestra per il
  // «Back» del telaio e per il tasto indietro del browser (G2b), nelle fasi
  // gioca e chiudi. «Stay» chiude e basta; «Leave» passa da `abbandona`.
  const modaleUscita = conferma === "leave" && (
    <ModaleConferma
      titolo="Leave the match?"
      testo="The match in progress is not saved and will be lost."
      azione="Leave"
      annulla="Stay"
      onAnnulla={() => setConferma("")}
      onConferma={() => {
        setConferma("");
        abbandona();
      }}
    />
  );

  const intestazione = (
    <>
      <h1 className="qpsc-page-title">Games</h1>
      <div className="qpsc-games-context">
        <span className="qpsc-games-context__team">{squadra}</span>
        {evento && (
          <span className="qpsc-games-context__game">
            {quando}
            {evento.opponent ? ` · vs ${evento.opponent}` : ""}
          </span>
        )}
      </div>
    </>
  );

  // IL RIFIUTO E L'ATTESA, DENTRO IL TELAIO — come EditMemberPage: una riga.
  if (stato !== "pronto" || !evento || annoOperativo == null || !disponibile) {
    const inAttesa = stato === "caricamento" || (stato === "pronto" && annoOperativo == null);
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        <h1 className="qpsc-page-title">Games</h1>
        <div className="qpsc-games-context">
          <span className="qpsc-games-context__team">{squadra}</span>
        </div>
        <p className="qpsc-body-text qpsc-body-text--muted">
          {inAttesa ? "Loading game…" : "This game is not available."}
        </p>
      </HomeShell>
    );
  }

  // ---- fase «riepilogo» --------------------------------------------------
  if (fase === "riepilogo" && record) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        {intestazione}
        <RiepilogoPartita
          record={record}
          nomi={nomi}
          quando={quando}
          squadra={squadra}
          avversario={evento.opponent}
        />
        <div className="qpsc-games-actions">
          <button type="button" className="qpsc-ghost-btn" onClick={modificaRecord}>
            <FaPen />
            Edit
          </button>
        </div>
      </HomeShell>
    );
  }

  // ---- fase «chiudi» -----------------------------------------------------
  if (fase === "chiudi") {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        {intestazione}
        <Avviso
          testo={
            modificaDaRecord
              ? "Changes are not saved until you press Save."
              : "Match not saved yet. Check the totals and press Save — leaving this page loses them."
          }
        />
        <TabellaFinale
          squadra={squadra}
          avversario={evento.opponent}
          score={score}
          righe={righe}
          onScore={cambiaScore}
          onRiga={cambiaRiga}
        />
        <div className="qpsc-games-save">
          {erroreSalvataggio && (
            <p className="qpsc-games-save__error" role="alert">
              {erroreSalvataggio}
            </p>
          )}
          <button type="button" className="qpsc-tool-btn" onClick={salva} disabled={salvataggio}>
            <FaSave />
            {salvataggio ? "Saving…" : "Save"}
          </button>
          {modificaDaRecord && (
            <button
              type="button"
              className="qpsc-ghost-btn"
              onClick={annullaModifica}
              disabled={salvataggio}
            >
              Cancel
            </button>
          )}
        </div>
        {modaleUscita}
      </HomeShell>
    );
  }

  // ---- fase «gioca» ------------------------------------------------------
  if (fase === "gioca" && partita) {
    return (
      <HomeShell backTo={tornaIndietro} showSwitch={false}>
        {intestazione}
        <Avviso testo="Match in progress — not saved. Keep the app open and save when the match is over." />

        <Cronometro
          cron={partita.cron}
          adesso={adesso}
          onAvvia={() => aggiorna((p, ora) => ({ ...p, cron: avviaCronometro(p.cron, ora) }))}
          onPausa={() => aggiorna((p, ora) => ({ ...p, cron: pausaCronometro(p.cron, ora) }))}
          onAvanza={(sec) => aggiorna((p) => ({ ...p, cron: avanzaCronometro(p.cron, sec) }))}
          onChiudiTempo={() => setConferma("half")}
          onChiudiPartita={() => setConferma("end")}
        />

        <CampoPartita
          giocatori={giocatoriPartita}
          partita={partita}
          onEntra={(id, pos) => aggiorna((p, ora) => entraInCampo(p, id, pos, ora))}
          onEsce={(id) => aggiorna((p, ora) => esceDalCampo(p, id, ora))}
          onSposta={(id, pos) => aggiorna((p) => spostaInCampo(p, id, pos))}
          onScambia={(a, b) => aggiorna((p, ora) => scambia(p, a, b, ora))}
          onTocco={(id) => {
            setAdesso(Date.now());
            setMenuPer(id);
          }}
        />

        <CronologiaEventi partita={partita} giocatori={giocatoriPartita} />

        {maiCominciata && (
          <div className="qpsc-games-actions">
            <button type="button" className="qpsc-ghost-btn" onClick={tornaAllaRosa}>
              <FaUndo />
              Back to squad
            </button>
          </div>
        )}

        {giocatoreDelMenu && (
          <MenuEvento
            giocatore={giocatoreDelMenu}
            partita={partita}
            adesso={adesso}
            onEvento={(id, tipo) => aggiorna((p, ora) => registraEvento(p, id, tipo, ora))}
            onAzzera={(id) => aggiorna((p) => azzeraEventiGiocatore(p, id))}
            onSposta={(id, dentro) =>
              aggiorna((p, ora) =>
                dentro ? entraInCampo(p, id, CENTRO_CAMPO, ora) : esceDalCampo(p, id, ora)
              )
            }
            onChiudi={() => setMenuPer(null)}
          />
        )}

        {conferma === "half" && (
          <ModaleConferma
            titolo="End 1st half?"
            testo="The clock stops. You can reshuffle the team before starting the 2nd half."
            azione="End 1st half"
            onAnnulla={() => setConferma("")}
            onConferma={() => {
              setConferma("");
              aggiorna((p, ora) => ({ ...p, cron: chiudiTempo(p.cron, ora) }));
            }}
          />
        )}

        {conferma === "end" && (
          <ModaleConferma
            titolo="End the match?"
            testo="The clock stops for good and you move on to the final score and the totals. You cannot come back to the pitch."
            azione="End match"
            onAnnulla={() => setConferma("")}
            onConferma={() => {
              setConferma("");
              terminaPartita();
            }}
          />
        )}

        {modaleUscita}
      </HomeShell>
    );
  }

  // ---- fase «prepara» (G1) -----------------------------------------------
  return (
    <HomeShell backTo={tornaIndietro} showSwitch={false}>
      {intestazione}

      <div className="qpsc-games-actions">
        <p className="qpsc-games-count">
          Selected: {convocati.size} of {players.length}
        </p>
        {/* Il ponte al campo (G2): porta i convocati già nello stato alla
            fase «gioca». Senza convocati non c'è partita da gestire. */}
        <button
          type="button"
          className="qpsc-tool-btn"
          onClick={avviaPartita}
          disabled={convocati.size === 0}
        >
          <FaPlay />
          Start match
        </button>
      </div>

      {players.length === 0 ? (
        <p className="qpsc-games-empty">No players in this team.</p>
      ) : (
        <div className="qpsc-games-roster">
          {players.map((p) => {
            const chiave = String(p.id);
            const st = statoDi(p.id);
            const voce = STATO_DI[st];

            return (
              <div key={chiave} className="qpsc-games-player">
                <label className="qpsc-check">
                  <input
                    className="qpsc-check__input"
                    type="checkbox"
                    checked={convocati.has(chiave)}
                    onChange={() => commuta(p.id)}
                  />
                  <span className="qpsc-check__box" aria-hidden="true" />
                  <span className="qpsc-games-player__body">
                    <span className="qpsc-games-player__name">{nomeDi(p)}</span>
                    <span className="qpsc-games-player__att">
                      <SegnoStato stato={st} />
                      {voce ? voce.etichetta : NESSUNA_RISPOSTA}
                    </span>
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </HomeShell>
  );
}

// ===========================================================================
// L'AVVISO «NON SALVATA» — sempre visibile nelle fasi gioca/chiudi
// (incarico G2 §6): ambra di attesa, icona Font Awesome, tono sobrio.
// ===========================================================================
function Avviso({ testo }) {
  return (
    <p className="qpsc-games-warning" role="status">
      <FaExclamationTriangle className="qpsc-games-warning__icon" aria-hidden="true" />
      <span>{testo}</span>
    </p>
  );
}

// ===========================================================================
// LA CRONOLOGIA DEGLI EVENTI — sola lettura sotto il campo: il coach vede
// ciò che ha registrato (tempo, minuto, tipo, giocatore). Le correzioni
// si fanno in tabella a fine partita (Strada A), non qui.
// ===========================================================================
function CronologiaEventi({ partita, giocatori }) {
  if (partita.eventi.length === 0) return null;

  const nomeDiId = (id) => {
    const g = giocatori.find((x) => x.id === id);
    return g ? g.nome : "Player";
  };

  const descrizione = (e) => {
    if (e.tipo === TIPI_EVENTO.GOL) return "Goal";
    if (e.tipo === TIPI_EVENTO.GIALLO) return "Yellow card";
    return e.automatico ? "Red card (2nd yellow)" : "Red card";
  };

  const icona = (e) => {
    if (e.tipo === TIPI_EVENTO.GOL) return <FaFutbol className="qpsc-games-log__icon--goal" />;
    if (e.tipo === TIPI_EVENTO.GIALLO) return <FaSquare className="qpsc-games-log__icon--yellow" />;
    return <FaSquare className="qpsc-games-log__icon--red" />;
  };

  return (
    <section className="qpsc-games-log" aria-label="Match events">
      <h2 className="qpsc-games-log__title">Events</h2>
      <ul className="qpsc-games-log__list">
        {[...partita.eventi].reverse().map((e) => (
          <li key={e.n} className="qpsc-games-log__item">
            <span className="qpsc-games-log__when">
              {e.tempo === 1 ? "1H" : "2H"} {e.minuto}&prime;
            </span>
            <span className="qpsc-games-log__icon" aria-hidden="true">
              {icona(e)}
            </span>
            <span className="qpsc-games-log__what">
              {descrizione(e)} &middot; {nomeDiId(e.playerId)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ===========================================================================
// LA CONFERMA — guscio modale di AdminTools.css (la forma di `ModaleStato`
// in AttendanceSummary.jsx): titolo, una riga, l'annullamento («Cancel», o
// «Stay» nella finestra d'uscita) e l'azione.
// ===========================================================================
function ModaleConferma({ titolo, testo, azione, annulla = "Cancel", onAnnulla, onConferma }) {
  return (
    <div className="qpsc-modal-overlay" onClick={onAnnulla}>
      <div
        className="qpsc-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="qpsc-modal__title">{titolo}</h2>
        <div className="qpsc-modal__body">
          <p className="qpsc-modal__note">{testo}</p>
        </div>
        <div className="qpsc-modal__actions">
          <button type="button" className="qpsc-ghost-btn" onClick={onAnnulla}>
            {annulla}
          </button>
          <button type="button" className="qpsc-tool-btn" onClick={onConferma}>
            {azione}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// IL SEGNO DI UNO STATO — copia di `SegnoStato` in AttendanceSummary.jsx:
// icona Font Awesome, colore dal token; «—» se nessuno stato.
// ===========================================================================
function SegnoStato({ stato }) {
  const voce = STATO_DI[stato];

  if (!voce) {
    return <span className="qpsc-games-mark qpsc-games-mark--none">&mdash;</span>;
  }

  const { Icon, etichetta } = voce;

  return (
    <span className={`qpsc-games-mark qpsc-games-mark--${stato}`} title={etichetta}>
      <Icon />
    </span>
  );
}
